<template>
  <div class="toast-container">
    <transition-group name="toast-animation" tag="div" class="toast-list">
      <div 
        v-for="toast in toasts" 
        :key="toast.id" 
        class="toast-item" 
        :class="toast.type"
      >
        <div class="toast-accent"></div>
        <div class="toast-icon">
          <CheckCircle v-if="toast.type === 'success'" :size="20" />
          <AlertCircle v-else-if="toast.type === 'error' || toast.type === 'warning'" :size="20" />
          <Info v-else :size="20" />
        </div>
        <div class="toast-content">
          <h4 v-if="toast.title" class="toast-title">{{ toast.title }}</h4>
          <p class="toast-message">{{ toast.message }}</p>
          <button 
            v-if="toast.type === 'error'" 
            class="toast-copy-btn" 
            @click="copyToClipboard(toast.message)"
          >
            <Copy :size="12" />
            <span>Copiar Detalhes</span>
          </button>
        </div>
        <button class="toast-close" @click="close(toast.id)">
          <X :size="16" />
        </button>
      </div>
    </transition-group>
  </div>
</template>
 
<script>
import { CheckCircle, AlertCircle, Info, X, Copy } from 'lucide-vue-next';
 
export default {
  name: 'ToastComp',
  components: {
    CheckCircle,
    AlertCircle,
    Info,
    X,
    Copy
  },
  computed: {
    toasts() {
      return this.$store.getters.activeToasts;
    }
  },
  methods: {
    close(id) {
      this.$store.dispatch('removeToast', id);
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        // Não criamos outro toast de sucesso que dure muito para não poluir, apenas 1.5s
        this.$store.dispatch('showToast', {
          title: 'Copiado!',
          message: 'Erro copiado com sucesso.',
          type: 'success',
          duration: 1500
        });
      } catch (err) {
        console.error('Erro ao copiar:', err);
      }
    }
  }
}
</script>

<style lang="less" scoped>
.toast-container {
  position: fixed;
  top: calc(16px + max(12px, env(safe-area-inset-top, 0px)));
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  max-width: 400px;
  z-index: 10000002; /* Float above everything including CartSheet */
  pointer-events: none;

  .toast-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  .toast-item {
    pointer-events: auto;
    display: flex;
    align-items: flex-start;
    background: rgba(10, 25, 47, 0.95);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 20px;
    padding: 14px 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.35);
    position: relative;
    overflow: hidden;
    transition: all 0.3s cubic-bezier(0.25, 1, 0.5, 1);
    max-height: 250px;
    overflow-y: auto;

    .toast-accent {
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 5px;
    }

    .toast-icon {
      margin-right: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      margin-top: 2px;
    }

    .toast-content {
      flex: 1;
      padding-right: 8px;
      text-align: left;

      .toast-title {
        color: white;
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 4px;
      }

      .toast-message {
        color: rgba(255, 255, 255, 0.9);
        font-size: 13px;
        font-weight: 500;
        line-height: 1.4;
        word-break: break-word;
      }

      .toast-copy-btn {
        margin-top: 10px;
        background: rgba(255, 255, 255, 0.15);
        border: 1px solid rgba(255, 255, 255, 0.25);
        color: white;
        border-radius: 8px;
        padding: 6px 12px;
        font-size: 11px;
        font-weight: 700;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        transition: all 0.2s;

        &:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        &:active {
          transform: scale(0.97);
        }
      }
    }

    .toast-close {
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.4);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      border-radius: 50%;
      transition: all 0.2s;

      &:hover {
        color: white;
        background: rgba(255, 255, 255, 0.1);
      }
    }

    /* Type styles */
    &.success {
      .toast-accent { background: var(--green); }
      .toast-icon { color: var(--green); }
    }

    &.error {
      .toast-accent { background: #ff3b30; }
      .toast-icon { color: #ff3b30; }
    }

    &.warning {
      .toast-accent { background: var(--orange); }
      .toast-icon { color: var(--orange); }
    }

    &.info {
      .toast-accent { background: var(--blue); }
      .toast-icon { color: var(--blue); }
    }
  }
}

/* Animations */
.toast-animation-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}

.toast-animation-enter-to {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.toast-animation-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.toast-animation-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.95);
}
</style>
